/**
 * Density sampling with 4 curve types: uniform, linear, exponential, bell.
 * Used to control object/feature density across map areas.
 */
export class DensitySampler {
  /**
   * @param {string} curve - 'uniform' | 'linear' | 'exponential' | 'bell'
   * @param {number} min - Minimum density (0-1)
   * @param {number} max - Maximum density (0-1)
   * @param {object} options - { center, spread, direction }
   */
  constructor(curve = 'uniform', min = 0, max = 1, options = {}) {
    this.curve = curve;
    this.min = min;
    this.max = max;
    this.center = options.center || 0.5;
    this.spread = options.spread || 0.3;
    this.direction = options.direction || 'horizontal'; // 'horizontal' | 'vertical' | 'radial'
  }

  /**
   * Sample density at normalized position (0-1).
   * @param {number} nx - Normalized x (0-1)
   * @param {number} ny - Normalized y (0-1)
   * @returns {number} Density value between min and max.
   */
  sample(nx, ny) {
    let t;
    switch (this.direction) {
      case 'horizontal': t = nx; break;
      case 'vertical': t = ny; break;
      case 'radial':
        t = Math.sqrt((nx - 0.5) ** 2 + (ny - 0.5) ** 2) * Math.SQRT2;
        break;
      default: t = nx;
    }

    let value;
    switch (this.curve) {
      case 'uniform':
        value = 1;
        break;
      case 'linear':
        value = t;
        break;
      case 'exponential':
        value = t * t;
        break;
      case 'bell':
        value = Math.exp(-0.5 * ((t - this.center) / this.spread) ** 2);
        break;
      default:
        value = 1;
    }

    return this.min + value * (this.max - this.min);
  }

  /**
   * Test whether a point should have content based on density + random.
   * @param {number} nx - Normalized x
   * @param {number} ny - Normalized y
   * @param {import('./SeededRandom.js').SeededRandom} rng
   * @returns {boolean}
   */
  test(nx, ny, rng) {
    return rng.next() < this.sample(nx, ny);
  }
}
